/**
 * SparkLayer <spark-form> overlay (open shadow).
 *
 * Type stack:
 *   1. Group legend (fieldset wrapping other fieldsets) — heading / 24px max
 *   2. Field legend (Name, Address, …) — Spark heading (untouched)
 *   3. Child labels — body (Geist / 400)
 *
 * Spark i18n maps "Last Name" → "Nom", colliding with the parent
 * legend. Rewrite last-name children. Re-apply after validation
 * re-renders. Theme CSS cannot pierce the shadow.
 */
(() => {
  const HOST_SEL = 'spark-form';
  const MARK = 'data-fixatech-spark-form-labels';
  const LAST_SEL = 'label[for$="_last_name"]';
  const DEBOUNCE_MS = 80;
  const COPY = {
    fr: { last: 'Nom de famille' },
    en: { last: 'Last name' },
  };
  const CSS =
    'fieldset:has(fieldset)>legend{' +
    'font-family:var(--spark-header-font,var(--font-heading--family,inherit))!important;' +
    'font-size:min(var(--font-h4--size,24px),24px)!important;' +
    'font-weight:var(--spark-header-font-weight,var(--font-heading--weight,700))!important;' +
    'letter-spacing:var(--spark-header-letter-spacing,var(--letter-spacing--heading-normal,0))!important;' +
    'line-height:var(--line-height--heading-normal,1.25)!important;' +
    '}' +
    'fieldset label{' +
    'font-family:var(--spark-font-default,var(--font-body--family,Geist,sans-serif))!important;' +
    'font-weight:var(--spark-font-weight-default,400)!important;' +
    'letter-spacing:0!important;' +
    'text-transform:none!important;' +
    '}';

  const locale = () => {
    const lang = (
      (document.documentElement && document.documentElement.lang) ||
      ''
    )
      .trim()
      .toLowerCase();
    return lang.indexOf('fr') === 0 ? 'fr' : 'en';
  };

  const lastCopy = () => COPY[locale()].last;

  const labelStem = (label) =>
    (label.textContent || '').replace(/\s*\*\s*$/u, '').trim();

  const setLabelCopy = (label, text) => {
    const nodes = label.childNodes;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (node.nodeType !== Node.TEXT_NODE) continue;
      const raw = node.textContent;
      if (!raw || !raw.trim()) continue;
      const star = raw.match(/\s*\*\s*$/u);
      const next = star ? text + star[0] : text;
      if (raw === next) return false;
      node.textContent = next;
      return true;
    }
    if (labelStem(label) === text) return false;
    label.insertBefore(document.createTextNode(text), label.firstChild);
    return true;
  };

  const injectCss = (root) => {
    let style = root.querySelector(`style[${MARK}]`);
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(MARK, '');
      root.appendChild(style);
    }
    if (style.textContent !== CSS) style.textContent = CSS;
  };

  const paintRoot = (root) => {
    if (!root) return;
    injectCss(root);
    const want = lastCopy();
    root.querySelectorAll(LAST_SEL).forEach((label) => {
      if (labelStem(label) === want) return;
      setLabelCopy(label, want);
    });
  };

  const pendingHosts = new Set();
  let paintAllPending = false;
  let paintTimer = 0;

  const flushPaint = () => {
    paintTimer = 0;
    if (paintAllPending) {
      paintAllPending = false;
      pendingHosts.clear();
      document.querySelectorAll(HOST_SEL).forEach((host) => {
        if (host.shadowRoot) paintRoot(host.shadowRoot);
        else waitForShadow(host);
      });
      return;
    }
    pendingHosts.forEach((host) => {
      if (host.shadowRoot) paintRoot(host.shadowRoot);
      else waitForShadow(host);
    });
    pendingHosts.clear();
  };

  const scheduleFlush = () => {
    if (paintTimer) return;
    paintTimer = setTimeout(flushPaint, DEBOUNCE_MS);
  };

  const scheduleHost = (host) => {
    pendingHosts.add(host);
    scheduleFlush();
  };

  const scheduleAll = () => {
    paintAllPending = true;
    scheduleFlush();
  };

  const waitingShadow = new WeakSet();
  const waitForShadow = (host, cb) => {
    if (host.shadowRoot) {
      if (cb) cb();
      else scheduleHost(host);
      return;
    }
    if (waitingShadow.has(host)) return;
    waitingShadow.add(host);
    const tryOnce = () => {
      if (!host.shadowRoot) return false;
      waitingShadow.delete(host);
      if (cb) cb();
      else scheduleHost(host);
      return true;
    };
    const afterDefined = () => {
      if (tryOnce()) return;
      requestAnimationFrame(() => {
        if (tryOnce()) return;
        setTimeout(() => {
          if (!tryOnce()) waitingShadow.delete(host);
        }, 200);
      });
    };
    const name = host.localName;
    if (window.customElements && customElements.get(name)) afterDefined();
    else if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined(name).then(afterDefined).catch(() => {});
    } else afterDefined();
  };

  const observedRoots = new WeakSet();
  const watchHostShadow = (host, root) => {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);
    new MutationObserver(() => scheduleHost(host)).observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  const paint = (host) => {
    if (!host.shadowRoot) return false;
    paintRoot(host.shadowRoot);
    watchHostShadow(host, host.shadowRoot);
    return true;
  };

  const watch = (host) => {
    if (host.shadowRoot) {
      paint(host);
      return;
    }
    waitForShadow(host, () => paint(host));
  };

  const hasHosts = () => Boolean(document.querySelector(HOST_SEL));

  const start = () => {
    if (!hasHosts()) return;
    scheduleAll();
    document.querySelectorAll(HOST_SEL).forEach((host) => {
      if (host.shadowRoot) watchHostShadow(host, host.shadowRoot);
    });
  };

  const arm = () => {
    if (hasHosts()) start();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();

  document.addEventListener('sparklayer:ready', () => {
    if (hasHosts()) scheduleAll();
  });

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('spark-form').then(arm).catch(() => {});
  }

  if (document.documentElement) {
    new MutationObserver((records) => {
      for (let i = 0; i < records.length; i++) {
        const added = records[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches && node.matches(HOST_SEL)) watch(node);
          else if (node.querySelector) {
            node.querySelectorAll(HOST_SEL).forEach(watch);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
